<template>
    <ListData :title="$t('menu.teams')" 
        :dataTable="dataTable" 
        :data="data" 
        :totalPages="totalPages"
        :loading="loading"
        :allowMultipleSelect="true"
        :isRowSelectable="canEditTeam"
        :showTagFilter="false"
        @fetchData="fetchData"
        @addNewData="createTeamPage"
        @deleteData="deleteSelected"
         />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import TriadEditIcon from "@/components/icons/TriadEditIcon.vue";
import TriadRemoveIcon from "@/components/icons/TriadRemoveIcon.vue";
import Pagination from '@/models/pagination';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import userStore, { verifyIam } from '@/store/user.store';
import { useRouter } from 'vue-router';

const router = useRouter();
const { t } = useI18n();

const loading = ref(true);
const reloadingTable = ref(false);
const filter = ref({
    pagination: new Pagination(1,10)
});

const totalPages = ref(0);

const dataTable = computed(() => ({
    columns: [

        { title: t('table.name'), field: 'name', rowStyle: 'w-full md:w-1/3 md:min-w-[200px] font-medium truncate overflow-hidden'},
        { title: t('table.parent'), field: (r) => r.parent?.name || '-', sortable: false, rowStyle: 'w-1/4 min-w-[160px] hidden md:table-cell text-gray-500'},
        { title: t('table.description'), field: 'description', rowStyle: 'w-1/5 max-w-[180px] truncate hidden lg:table-cell text-gray-500'},
        { title: t('table.owner'), field: (r) => r.owner ? t('form.yes') : t('form.no'), sortable: false, rowStyle: 'w-16 hidden sm:table-cell'},
        { title: t('table.members'), field: (r) =>  r.members ? r.members.length : 0 , sortable: false, rowStyle: 'w-16 hidden sm:table-cell'},
        { 
            properties: () => ({
                tooltip: t('table.edit')
            }),
            component: TriadEditIcon,
            action: (element) => {
                router.push({path: `/team/${element._id}`})
            },
            disabled: (element) => !canEditTeam(element),
            rowStyle: 'w-[50px] max-w-[50px]'
        },
        { 
            properties: () => ({
                tooltip: t('table.delete')
            }),
            component: TriadRemoveIcon,
            action: (element) => {
                deleteData(element._id)
            },
            disabled: (element) => !canEditTeam(element),
            rowStyle: 'w-[50px] max-w-[50px]'
        },
    ]
}));
const data = ref([])

// Edição/exclusão: dono ou admin (mesmo critério do router guard).
// `owner === false` explícito bloqueia; flag ausente mantém legado.
const canManageAllTeams = computed(() => {
    try {
        return verifyIam(userStore.state.userInfo, '/team');
    } catch {
        return false;
    }
});
const canEditTeam = (row) => row?.owner !== false || !!canManageAllTeams.value;

const fetchData = async (f) => {
    filter.value = f || filter.value;
    const query = Object.entries(filter.value).map( f => {
            if (typeof(f[1]) == 'object'){
                return f[1]
            } else {
                return f[0]+'='+f[1]
            }
        }).join('&');
    const response = await fetch(`/api/team?${query}`)
    if  (response.status === 200) {
        data.value = await response.json()
        totalPages.value = data.value.totalPages
    } else {
        toastStore.dispatch('addMessage', {message: t('toast.errorFetchingData'), icon: 'error'})
    }
    loading.value = false;
    reloadingTable.value = false;
}

const deleteData = async (id) => {
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete this team?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/team/${id}`, {
            method: 'DELETE'
        })
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: t('toast.teamDeleted'), icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: t('toast.errorDeletingTeam'), icon: 'error'})
            reloadingTable.value = false;
        }
    }
}

const createTeamPage = () => {
    router.push({path: `/team/new`})
}

const deleteSelected = async (elements) => {
    const owned = (elements || []).filter(canEditTeam);
    if (owned.length === 0) {
        toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction') || 'Not allowed', icon: 'warning', ttl: 3000 });
        return;
    }
    if (owned.length < (elements || []).length) {
        toastStore.dispatch('addMessage', { message: t('artistInfo.notOwnerAction') || 'Not allowed', icon: 'warning', ttl: 3000 });
    }
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to delete all these teams?')) {
        reloadingTable.value = true;
        const response = await fetch(`/api/team/delete`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ids: owned.map(e => e.id || e._id)
            })
        })
        if (response.status === 200) {
            toastStore.dispatch('addMessage', {message: t('toast.teamDeleted'), icon: 'success'})
            fetchData()
        } else {
            toastStore.dispatch('addMessage', {message: t('toast.errorDeletingTeam'), icon: 'error'})
            reloadingTable.value = false;
        }
    }  
}
</script> 